/**
 * Email Polling Handler
 *
 * Polls IMAP for new emails and emits them to the EventBus.
 * Each new email goes through the funnel:
 *   IMAP → PollingService → EventBus → TriggerMatcher + NotificationRouter
 */

import { listMessages, getMessage, getImapStatus } from '../../../services/imapClient';
import type { PollingSource } from '../types';
import type { PolledItem } from '../pollingService';

// Throttle error logging to prevent flooding client logs
let consecutiveFailures = 0;
let lastErrorMessage = '';
const ERROR_LOG_INTERVAL = 10; // Log every Nth consecutive failure

/**
 * Poll IMAP inbox for new emails
 */
export async function emailPollingHandler(source: PollingSource): Promise<PolledItem[]> {
  const items: PolledItem[] = [];

  try {
    const accountId = source.config?.accountId as string | undefined;

    // Check if IMAP is connected
    const status = await getImapStatus(accountId);
    if (!status.imap.connected) {
      // Silent skip - IMAP auto-reconnect handles this server-side
      return items;
    }

    // Get folder from source config, default to INBOX
    const folder = (source.config?.folder as string) || 'INBOX';
    const limit = (source.config?.limit as number) || 20;

    // Fetch recent messages using accountId
    const messages = await listMessages(folder, limit, 0, accountId);

    // Filter to only new messages (uid > lastSeenId)
    const lastSeenUid = source.lastSeenId ? parseInt(source.lastSeenId, 10) : 0;

    for (const msg of messages) {
      // Skip already-seen messages
      if (msg.uid <= lastSeenUid) {
        continue;
      }

      // Get full message body for classification
      let body = msg.snippet || '';
      try {
        const fullMsg = await getMessage(msg.uid, folder, accountId);
        body = fullMsg.body || fullMsg.bodyHtml || msg.snippet || '';
      } catch {
        // Message fetch failed, use snippet only
      }

      // Create polled item
      items.push({
        id: String(msg.uid),
        eventType: 'email.received',
        entityType: 'email',
        data: {
          uid: msg.uid,
          from: msg.from?.address || '',
          fromName: msg.from?.name || '',
          to: msg.to.map(t => t.address),
          subject: msg.subject || '(no subject)',
          body,
          snippet: msg.snippet,
          date: msg.date,
          folder,
          hasAttachments: msg.hasAttachments,
          flags: msg.flags,
          accountId,
        },
      });
    }

    // Recovery: log if we were previously failing
    if (consecutiveFailures > 0) {
      console.log(`[EmailPolling] Recovered after ${consecutiveFailures} failed attempts`);
      consecutiveFailures = 0;
      lastErrorMessage = '';
    }

    if (items.length > 0) {
      console.log(`[EmailPolling] Found ${items.length} new email(s)`);
    }

  } catch (err) {
    consecutiveFailures++;
    const msg = err instanceof Error ? err.message : String(err);

    // Only log on first failure or every Nth occurrence to prevent log flooding
    if (consecutiveFailures === 1 || consecutiveFailures % ERROR_LOG_INTERVAL === 0) {
      console.warn(`[EmailPolling] Connection issue (attempt ${consecutiveFailures}): ${msg}`);
    }

    lastErrorMessage = msg;
  }

  return items;
}

/**
 * Default email polling source configuration
 */
export function createEmailPollingSource(options?: {
  id?: string;
  name?: string;
  folder?: string;
  intervalMs?: number;
  accountId?: string;
}): PollingSource {
  return {
    id: options?.id || 'email-inbox',
    name: options?.name || 'Email INBOX',
    type: 'email',
    enabled: true,
    intervalMs: options?.intervalMs || 30000, // 30 seconds default
    config: {
      folder: options?.folder || 'INBOX',
      limit: 20,
      accountId: options?.accountId,
    },
  };
}
